<script setup lang="ts">
import FinestKpiCard from '@/components/FinestKpiCard.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData

const props = defineProps<{
  estateObjects: EstateObjectData[]
}>()

const { t, n } = useI18n()

const toNumber = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value)

const sum = (pick: (estateObject: EstateObjectData) => number | null | undefined): number =>
  props.estateObjects.reduce((total, estateObject) => total + (pick(estateObject) ?? 0), 0)

const units = computed(() => sum((estateObject) => estateObject.number_of_units))

const vacantUnits = computed(() => sum((estateObject) => estateObject.number_of_units_empty))

const portfolioValue = computed(() => sum((estateObject) => toNumber(estateObject.purchase_price)))

const monthlyRent = computed(() => sum((estateObject) => toNumber(estateObject.actual_rent)))

const averageGrossYield = computed(() => {
  const yields = props.estateObjects
    .map((estateObject) => {
      const price = toNumber(estateObject.purchase_price)
      const rent = toNumber(estateObject.actual_rent)

      return rent !== null && price !== null && price > 0 ? (rent * 12) / price : null
    })
    .filter((value): value is number => value !== null)

  return yields.length ? yields.reduce((total, value) => total + value, 0) / yields.length : null
})

const vacancyRate = computed(() => (units.value ? vacantUnits.value / units.value : null))

const MILLION = 1_000_000

const portfolioValueKpi = computed(() =>
  portfolioValue.value >= MILLION
    ? { value: portfolioValue.value / MILLION, format: 'number' as const, suffix: t('millionSuffix') }
    : { value: portfolioValue.value, format: 'currency' as const, suffix: '' },
)
</script>

<template>
  <div class="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
    <FinestKpiCard
      :title="t('objects.title')"
      :value="estateObjects.length"
      format="number"
      :subtitle="t('objects.subtitle', { units })" />

    <FinestKpiCard
      :title="t('value.title')"
      :value="portfolioValueKpi.value"
      :format="portfolioValueKpi.format"
      :suffix="portfolioValueKpi.suffix"
      :maximum-fraction-digits="portfolioValueKpi.suffix ? 2 : 0"
      :subtitle="t('value.subtitle')" />

    <FinestKpiCard
      :title="t('rent.title')"
      :value="monthlyRent"
      format="currency"
      :maximum-fraction-digits="0"
      :subtitle="t('rent.subtitle', { yearly: n(monthlyRent * 12, 'euros') })" />

    <FinestKpiCard
      :title="t('yield.title')"
      :value="averageGrossYield ?? t('empty')"
      format="percent"
      :minimum-fraction-digits="1"
      :maximum-fraction-digits="1"
      :subtitle="t('yield.subtitle')" />

    <FinestKpiCard
      :title="t('vacancy.title')"
      :value="vacancyRate ?? t('empty')"
      format="percent"
      :maximum-fraction-digits="1"
      :subtitle="t('vacancy.subtitle', { vacant: vacantUnits, units })" />
  </div>
</template>

<i18n>
{
  "de": {
    "empty": "—",
    "millionSuffix": " Mio €",
    "objects": {
      "title": "Objekte",
      "subtitle": "{units} Einheiten"
    },
    "value": {
      "title": "Portfoliowert",
      "subtitle": "Summe Kaufpreise"
    },
    "rent": {
      "title": "Ist-Miete p.M.",
      "subtitle": "{yearly} p.a."
    },
    "yield": {
      "title": "Ø Bruttorendite",
      "subtitle": "über alle Objekte"
    },
    "vacancy": {
      "title": "Leerstand",
      "subtitle": "{vacant} von {units} Einheiten"
    }
  },
  "en": {
    "empty": "—",
    "millionSuffix": "M €",
    "objects": {
      "title": "Properties",
      "subtitle": "{units} units"
    },
    "value": {
      "title": "Portfolio value",
      "subtitle": "Sum of purchase prices"
    },
    "rent": {
      "title": "Actual rent p.m.",
      "subtitle": "{yearly} p.a."
    },
    "yield": {
      "title": "Ø Gross yield",
      "subtitle": "across all properties"
    },
    "vacancy": {
      "title": "Vacancy",
      "subtitle": "{vacant} of {units} units"
    }
  }
}
</i18n>
